// Page Synthese Finale - Refonte complete
// Inclut: HALTES consolide, detail par metier, progression Q2, distribution par metier

import { useEffect, useState, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui'
import { trackEvent } from '../lib/analytics'
import { DiagnosticLayout, useDiagnosticSteps, useDiagnosticData, useDiagnosticProgress } from '../components/layout'
import { ScoreProgressBar } from '../components/results'
import { SEUILS_CASQUES, getCasques, getCasquesAffiches, type Casque } from '@autodiag/shared'
import RadarChartManagement, {
  MANAGEMENT_AXES,
  type ScoresManagement,
} from '../components/results/RadarChartManagement'
import HaltesConsolidatedAnalysis, { type MetierScoreData } from '../components/results/HaltesConsolidatedAnalysis'
import MetierSynthesisCard, { type ThemeScore } from '../components/results/MetierSynthesisCard'
import ManagementProgressList from '../components/results/ManagementProgressList'
import type { Distribution } from '../components/results/DistributionChart'
import { api } from '../services/api'
import type { ScoresAxes, MaxParAxe } from '../components/results/RadarChart'

interface ReponseData {
  questionId: string
  metierId?: string
  valeur: number
}

// Axes HALTES
const HALTES_AXES = ['H', 'A', 'LT', 'E', 'S'] as const


export default function SyntheseFinalePage() {
  const { id: diagnosticId } = useParams<{ id: string }>()
  const navigate = useNavigate()

  // Charger les donnees du diagnostic
  const { metierIds, metiersSelectionnes, diagnosticState, isLoadingData } = useDiagnosticData(diagnosticId)
  const diagnosticProgress = useDiagnosticProgress(diagnosticId)

  const [reponses, setReponses] = useState<ReponseData[]>([])
  const [questionsQ1, setQuestionsQ1] = useState<any[]>([])
  const [questionsQ2, setQuestionsQ2] = useState<any[]>([])
  const [actions, setActions] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [entrepriseNom, setEntrepriseNom] = useState<string>('')
  const [entrepriseLogo, setEntrepriseLogo] = useState<string | null>(null)
  const [diagnosticDate, setDiagnosticDate] = useState<string>('')
  const [diagnosticVersion, setDiagnosticVersion] = useState<number>(1)
  const [attestation, setAttestation] = useState<{ statut: string; badgeDore: boolean } | null>(null)
  // Generer les etapes de progression
  const steps = useDiagnosticSteps('synthese-finale', diagnosticId, {
    totalMetiers: metierIds.length || 1,
    currentMetierIndex: Math.max(0, metierIds.length - 1),
    q2Completed: true,
    metiersSelectionnes: metiersSelectionnes.map(m => ({ code: m.code, nom: m.nom })),
  }, diagnosticProgress)

  // Charger les donnees
  useEffect(() => {
    async function loadData() {
      if (!diagnosticId) return

      setIsLoading(true)

      try {
        // Charger le diagnostic complet + questions en parallèle
        const [diagnostic, q1, q2] = await Promise.all([
          api.getDiagnostic(diagnosticId),
          api.getQuestionsQ1(),
          api.getQuestionsQ2(),
        ])

        if (diagnostic) {
          const dateStr = diagnostic.dateDebut || diagnostic.createdAt
          setDiagnosticDate(dateStr ? new Date(dateStr).toLocaleDateString('fr-FR') : '')
          setDiagnosticVersion(diagnostic.versionNumber || 1)

          // Attestation depuis le diagnostic API
          if (diagnostic.attestation) {
            setAttestation(diagnostic.attestation)
          }

          // Entreprise depuis le diagnostic API
          if (diagnostic.entreprise) {
            setEntrepriseNom(diagnostic.entreprise.raisonSociale || '')
            if (diagnostic.entreprise.logo) {
              setEntrepriseLogo(diagnostic.entreprise.logo)
            }
          }

          // Reponses depuis le diagnostic API (normaliser metierId → code)
          setReponses((diagnostic.reponses || []).map((r: any) => ({
            ...r,
            metierId: r.metier?.code || r.metierId,
          })))

          // Actions depuis le diagnostic API
          setActions(diagnostic.actions || [])
        }

        setQuestionsQ1(q1)
        setQuestionsQ2(q2)
      } catch (error) {
        console.error('Erreur chargement synthese finale:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [diagnosticId])

  // Distribution globale des reponses
  const distribution = useMemo((): Distribution => {
    const dist: Distribution = { 0: 0, 1: 0, 2: 0, 3: 0 }
    reponses.forEach((r) => {
      const val = Math.min(3, Math.max(0, Math.round(r.valeur))) as 0 | 1 | 2 | 3
      dist[val] += 1
    })
    return dist
  }, [reponses])

  // Distribution par metier
  const distributionByMetier = useMemo(() => {
    const result = new Map<string, Distribution>()

    metierIds.forEach((metierId) => {
      const dist: Distribution = { 0: 0, 1: 0, 2: 0, 3: 0 }
      reponses
        .filter(r => r.metierId === metierId)
        .forEach((r) => {
          const val = Math.min(3, Math.max(0, Math.round(r.valeur))) as 0 | 1 | 2 | 3
          dist[val] += 1
        })
      result.set(metierId, dist)
    })

    return result
  }, [metierIds, reponses])

  // Seuils pour le nombre de metiers
  const nbMetiers = Math.min(3, Math.max(1, metierIds.length)) as 1 | 2 | 3
  const seuilsActuels = SEUILS_CASQUES[nbMetiers]

  // Scores Q1 par metier (avec themes)
  const scoresQ1ByMetier = useMemo(() => {
    const result = new Map<string, {
      scores: ScoresAxes
      maxScores: MaxParAxe
      total: number
      max: number
      themeScores: ThemeScore[]
    }>()

    metierIds.forEach((metierId) => {
      const scores: ScoresAxes = { H: 0, A: 0, LT: 0, E: 0, S: 0 }
      const counts: ScoresAxes = { H: 0, A: 0, LT: 0, E: 0, S: 0 }
      const themesMap = new Map<string, { score: number; max: number; axe: string }>()

      const metierReponses = reponses.filter(r => r.metierId === metierId)

      metierReponses.forEach((reponse) => {
        const question = questionsQ1.find(q => q.id === reponse.questionId)
        if (question) {
          const axe = (question.axe || question.axeHaltes) as keyof ScoresAxes
          if (axe && HALTES_AXES.includes(axe as typeof HALTES_AXES[number])) {
            scores[axe] += reponse.valeur
            counts[axe] += 1
          }

          // Calcul par theme
          const themeName = question.themeQuestion
            || (typeof question.theme === 'object' && question.theme !== null ? question.theme.nom : null)
            || question.theme
            || 'Autre'

          if (!themesMap.has(themeName)) {
            themesMap.set(themeName, { score: 0, max: 0, axe: axe || 'H' })
          }
          const current = themesMap.get(themeName)!
          current.score += reponse.valeur
          current.max += 3
        }
      })

      const maxScores: MaxParAxe = {
        H: counts.H * 3,
        A: counts.A * 3,
        LT: counts.LT * 3,
        E: counts.E * 3,
        S: counts.S * 3,
      }

      const themeScores: ThemeScore[] = Array.from(themesMap.entries()).map(([themeName, data]) => ({
        themeId: themeName.toLowerCase().replace(/\s+/g, '-'),
        themeName,
        axeHaltes: data.axe,
        score: data.score,
        max: data.max,
        percent: data.max > 0 ? Math.round((data.score / data.max) * 100) : 0,
      }))

      const total = Object.values(scores).reduce((a, b) => a + b, 0)
      const max = Object.values(maxScores).reduce((a, b) => a + b, 0)

      result.set(metierId, { scores, maxScores, total, max, themeScores })
    })

    return result
  }, [metierIds, reponses, questionsQ1])

  // Donnees pour HALTES consolide
  const haltesConsolidatedData = useMemo((): MetierScoreData[] => {
    return metiersSelectionnes.map((metier) => {
      const data = scoresQ1ByMetier.get(metier.code)
      return {
        metierId: metier.code,
        metierNom: metier.nom,
        scores: data?.scores || { H: 0, A: 0, LT: 0, E: 0, S: 0 },
        maxScores: data?.maxScores || { H: 0, A: 0, LT: 0, E: 0, S: 0 },
      }
    })
  }, [metiersSelectionnes, scoresQ1ByMetier])

  // Scores Q2 (management)
  const scoresQ2Data = useMemo(() => {
    const scores: ScoresManagement = {
      orientations: 0,
      roles: 0,
      gestion: 0,
      animation: 0,
      bilan: 0,
      audits: 0,
    }
    const counts: ScoresManagement = {
      orientations: 0,
      roles: 0,
      gestion: 0,
      animation: 0,
      bilan: 0,
      audits: 0,
    }

    const q2Reponses = reponses.filter(r => !r.metierId)

    q2Reponses.forEach((reponse) => {
      const question = questionsQ2.find(q => q.id === reponse.questionId)
      if (question && question.sousMethode) {
        const axeConfig = MANAGEMENT_AXES.find(a => a.code === question.sousMethode)
        if (axeConfig) {
          const axeKey = axeConfig.key
          scores[axeKey] += reponse.valeur
          counts[axeKey] += 1
        }
      }
    })

    const maxScores: ScoresManagement = {
      orientations: counts.orientations * 3,
      roles: counts.roles * 3,
      gestion: counts.gestion * 3,
      animation: counts.animation * 3,
      bilan: counts.bilan * 3,
      audits: counts.audits * 3,
    }

    return { scores, maxScores }
  }, [reponses, questionsQ2])

  // Scores Q2 par thème (pour Top/Flop) - même logique que Synthese2Page
  const themeScoresQ2 = useMemo(() => {
    const themesMap = new Map<string, { score: number; max: number }>()

    const q2Reponses = reponses.filter(r => !r.metierId)

    q2Reponses.forEach((reponse) => {
      const question = questionsQ2.find(q => q.id === reponse.questionId)
      if (question) {
        // Même logique que Synthese2Page : themeQuestion > theme.nom > theme (string) > fallback
        const themeName = question.themeQuestion
          || (typeof question.theme === 'object' && question.theme !== null ? question.theme.nom : null)
          || (typeof question.theme === 'string' ? question.theme : null)
          || 'Thème inconnu'

        if (!themesMap.has(themeName)) {
          themesMap.set(themeName, { score: 0, max: 0 })
        }
        const current = themesMap.get(themeName)!
        current.score += reponse.valeur
        current.max += 3
      }
    })

    // Convertir en tableau avec pourcentages
    return Array.from(themesMap.entries()).map(([themeName, data]) => ({
      themeId: (themeName || 'theme').toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
      themeName: themeName || 'Thème inconnu',
      score: data.score,
      max: data.max,
      percent: data.max > 0 ? Math.round((data.score / data.max) * 100) : 0,
    }))
  }, [reponses, questionsQ2])

  // Top 3 / Flop 3 pour Q2
  const q2Rankings = useMemo(() => {
    if (themeScoresQ2.length === 0) {
      return { top3: [], flop3: [] }
    }
    const sorted = [...themeScoresQ2].sort((a, b) => b.percent - a.percent)
    const top3 = sorted.slice(0, Math.min(3, sorted.length))
    // Éviter les doublons entre top et flop
    const remaining = sorted.slice(Math.min(3, sorted.length))
    const flop3 = remaining.slice(-Math.min(3, remaining.length)).reverse()
    return { top3, flop3 }
  }, [themeScoresQ2])

  // Score global
  const globalScores = useMemo(() => {
    let scoreQ1 = 0
    let maxQ1 = 0

    scoresQ1ByMetier.forEach((data) => {
      scoreQ1 += data.total
      maxQ1 += data.max
    })

    const scoreQ2 = Object.values(scoresQ2Data.scores).reduce((a, b) => a + b, 0)
    const maxQ2 = Object.values(scoresQ2Data.maxScores).reduce((a, b) => a + b, 0)

    return {
      scoreTotal: scoreQ1 + scoreQ2,
      maxTotal: maxQ1 + maxQ2,
      scoreQ1,
      maxQ1,
      scoreQ2,
      maxQ2,
    }
  }, [scoresQ1ByMetier, scoresQ2Data])

  // Calcul des casques — direct, plus de cap
  const casques = useMemo((): Casque => {
    if (reponses.length === 0) return 1
    return getCasques(globalScores.scoreTotal, nbMetiers)
  }, [globalScores.scoreTotal, nbMetiers, reponses.length])

  // Casques affiches (cap a 4 si score=5 sans attestation validee)
  const displayInfo = useMemo(() => {
    return getCasquesAffiches(casques, attestation)
  }, [casques, attestation])

  // Verifier DUERP (Q2-03) — "Non fait" (valeur 0) bloque l'attestation
  const duerpNonFait = useMemo(() => {
    const duerpReponse = reponses.find((r) => {
      const question = questionsQ2.find(q => q.id === r.questionId)
      return question?.code === 'Q2-03'
    })
    return !duerpReponse || duerpReponse.valeur === 0
  }, [reponses, questionsQ2])

  // Stats actions
  const actionsStats = useMemo(() => {
    const total = actions.length
    const completed = actions.filter(a => a.termineLe).length
    return { total, completed, pending: total - completed }
  }, [actions])

  // Navigation
  const handlePrint = () => {
    trackEvent('imprimer synthese finale')
    window.print()
  }

  const handleGoToAttestation = () => {
    trackEvent('generer attestation')
    navigate(`/diagnostic/${diagnosticId}/attestation`, { state: diagnosticState })
  }

  const handleGoBack = () => {
    navigate(`/diagnostic/${diagnosticId}/actions`, { state: diagnosticState })
  }

  if (isLoading || isLoadingData) {
    return (
      <DiagnosticLayout
        steps={steps}
        showNavigation={false}

        diagnosticState={diagnosticState}
      >
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-muted-foreground">Préparation de la synthèse finale...</p>
          </div>
        </div>
      </DiagnosticLayout>
    )
  }

  return (
    <DiagnosticLayout
      steps={steps}
      onBack={handleGoBack}
      onNext={handleGoToAttestation}
      backLabel="Plan d'actions"
      nextLabel="Générer l'attestation"

      diagnosticState={diagnosticState}
    >
      {/* Styles d'impression */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .print-area, .print-area * { visibility: visible; }
          .print-area { position: absolute; left: 0; top: 0; width: 100%; }
          .no-print { display: none !important; }
          .print-break-before { page-break-before: always; }
          .print-avoid-break { page-break-inside: avoid; }
          .shadow-sm, .shadow-lg { box-shadow: none !important; }
          svg { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
        }
      `}</style>

      <div className="print-area space-y-6">
        {/* Header de page - meme pattern que les autres pages */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Synthèse Finale</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Résultats complets pour {metiersSelectionnes.map(m => m.nom).join(', ')}
              </p>
            </div>
          </div>
        </div>

        {/* SECTION 1: Casques + Score global + Entreprise */}
        <div className="grid gap-6 lg:grid-cols-3 print-avoid-break">
          {/* Colonne principale (2/3) - Casques et scores */}
          <div className="lg:col-span-2">
            <Card className="h-full overflow-hidden py-0">
              {/* Header style wizard avec metiers visibles */}
              <div className="flex items-center justify-between px-5 py-4 bg-muted/50 border-b border-border">
                <div className="flex items-center gap-3">
                  <span className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold">
                    Résultats
                  </span>
                  <span className="text-border">|</span>
                  <div className="flex items-center gap-2">
                    {metiersSelectionnes.map((metier, index) => (
                      <span
                        key={metier.code}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-background border border-border text-foreground text-sm font-medium shadow-sm"
                      >
                        {metierIds.length > 1 && (
                          <span className="w-4 h-4 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">
                            {index + 1}
                          </span>
                        )}
                        {metier.nom}
                      </span>
                    ))}
                  </div>
                </div>
                <span className="text-sm text-muted-foreground">{reponses.length} réponses</span>
              </div>

              <CardContent className="p-5 sm:p-6">
                {/* Layout principal : Casques (2/3) + Score global (1/3) */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                  {/* Section Casques - 2/3 */}
                  <div className="lg:col-span-2 flex flex-col justify-center">
                    {/* Casques visuels — 5 casques */}
                    <div className="flex justify-center gap-3 sm:gap-4 mb-4">
                      {Array.from({ length: 5 }).map((_, index) => {
                        const isActive = index < displayInfo.casques

                        return (
                          <img
                            key={index}
                            src={isActive ? '/images/casque-actif.svg' : '/images/casque-desactive.svg'}
                            alt={isActive ? 'Casque actif' : 'Casque inactif'}
                            className="w-12 h-12 sm:w-16 sm:h-16 transition-all duration-300"
                          />
                        )
                      })}
                      {displayInfo.badgeDore && (
                        <img
                          src="/images/medaille-dore.svg"
                          alt="Badge doré"
                          className="w-10 h-10 sm:w-14 sm:h-14 ml-1 transition-all duration-300"
                        />
                      )}
                    </div>
                    {/* Texte résultat casques */}
                    <div className="text-center">
                      <div className="text-2xl sm:text-3xl font-bold text-foreground">
                        {displayInfo.casques} casque{displayInfo.casques !== 1 ? 's' : ''} sur 5
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {displayInfo.eligible5eme
                          ? 'Éligible au 5e casque — certification requise'
                          : `Niveau de prévention ${displayInfo.casques >= 4 ? 'excellent' : displayInfo.casques >= 3 ? 'en progression' : 'à améliorer'}`
                        }
                      </p>
                    </div>

                    {/* Barre de progression avec seuils - sous les casques */}
                    <div className="mt-6 w-full px-4">
                      <ScoreProgressBar
                        score={globalScores.scoreTotal}
                        seuils={seuilsActuels}
                        compact={false}
                      />
                    </div>
                  </div>

                  {/* Section Score Global - 1/3 */}
                  <div className="lg:border-l lg:border-border lg:pl-6 flex flex-col items-center justify-center">
                    {/* Chart circulaire */}
                    <div className="relative w-28 h-28 sm:w-32 sm:h-32 mb-3">
                      <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                        <circle
                          cx="50"
                          cy="50"
                          r="42"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="12"
                          className="text-muted"
                        />
                        <circle
                          cx="50"
                          cy="50"
                          r="42"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="12"
                          strokeLinecap="round"
                          strokeDasharray={`${(globalScores.maxTotal > 0 ? (globalScores.scoreTotal / globalScores.maxTotal) : 0) * 264} 264`}
                          className="text-primary transition-all duration-500"
                        />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-2xl sm:text-3xl font-bold text-primary">
                          {globalScores.maxTotal > 0 ? Math.round((globalScores.scoreTotal / globalScores.maxTotal) * 100) : 0}%
                        </span>
                      </div>
                    </div>
                    <div className="text-center mb-4">
                      <span className="text-base font-semibold text-foreground">Score Global</span>
                      <div className="text-sm text-muted-foreground">
                        {globalScores.scoreTotal} / {globalScores.maxTotal} pts
                      </div>
                    </div>

                    {/* Séparateur */}
                    <div className="w-full h-px bg-gray-200 mb-4"></div>

                    {/* Questionnaires Q1/Q2 */}
                    <div className="w-full space-y-3">
                      {/* Q1 - Risques */}
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded font-semibold">Q1</span>
                            <span className="text-sm text-gray-700 font-medium">Risques</span>
                          </div>
                          <span className="text-sm font-bold text-blue-700">
                            {globalScores.maxQ1 > 0 ? Math.round((globalScores.scoreQ1 / globalScores.maxQ1) * 100) : 0}%
                          </span>
                        </div>
                        <div className="h-2 bg-blue-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-500 transition-all"
                            style={{ width: `${globalScores.maxQ1 > 0 ? Math.round((globalScores.scoreQ1 / globalScores.maxQ1) * 100) : 0}%` }}
                          />
                        </div>
                      </div>

                      {/* Q2 - Management */}
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-purple-600 bg-purple-50 px-2 py-0.5 rounded font-semibold">Q2</span>
                            <span className="text-sm text-gray-700 font-medium">Management</span>
                          </div>
                          <span className="text-sm font-bold text-purple-700">
                            {globalScores.maxQ2 > 0 ? Math.round((globalScores.scoreQ2 / globalScores.maxQ2) * 100) : 0}%
                          </span>
                        </div>
                        <div className="h-2 bg-purple-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-purple-500 transition-all"
                            style={{ width: `${globalScores.maxQ2 > 0 ? Math.round((globalScores.scoreQ2 / globalScores.maxQ2) * 100) : 0}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Colonne secondaire (1/3) - Entreprise et actions */}
          <div className="space-y-4">
            {/* Card Entreprise */}
            <Card className="h-full">
              <CardContent className="p-5 flex flex-col h-full">
                {/* Logo entreprise */}
                <div className="flex justify-center mb-4">
                  {entrepriseLogo ? (
                    <div className="w-24 h-24 rounded-xl flex items-center justify-center bg-white border border-gray-200 p-2">
                      <img
                        src={entrepriseLogo}
                        alt={`Logo ${entrepriseNom}`}
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                  ) : (
                    <div className="w-24 h-24 bg-gray-100 rounded-xl flex items-center justify-center border-2 border-dashed border-gray-300">
                      <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                  )}
                </div>

                {/* Nom entreprise */}
                <div className="text-center mb-4">
                  <h3 className="text-lg font-bold text-gray-900">
                    {entrepriseNom || 'Mon Entreprise'}
                  </h3>
                  {diagnosticDate && (
                    <p className="text-sm text-gray-500 mt-1">
                      Diagnostic du {diagnosticDate}
                    </p>
                  )}
                </div>

                {/* Spacer pour pousser les boutons en bas */}
                <div className="flex-1" />

                {/* Bouton d'action + Version */}
                <div className="space-y-3">
                  <button
                    onClick={handlePrint}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg font-medium transition-colors text-sm"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Télécharger ma synthèse
                  </button>

                  {/* Version du diagnostic */}
                  <div className="text-center pt-2 border-t border-gray-100">
                    <span className="text-xs text-gray-400">Version {diagnosticVersion}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* SECTION 3 & 4: Detail par metier (2/3) + Management Q2 (1/3) */}
        <div className="print-break-before grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Detail par metier - 2/3 */}
          <div className="lg:col-span-2">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              Résultats Détaillés par Métier
            </h2>
            <div className={`grid gap-4 ${metierIds.length === 1 ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
              {metiersSelectionnes.map((metier) => {
                const data = scoresQ1ByMetier.get(metier.code)
                if (!data) return null

                return (
                  <MetierSynthesisCard
                    key={metier.code}
                    metier={metier}
                    scores={data.scores}
                    maxScores={data.maxScores}
                    totalScore={data.total}
                    maxTotal={data.max}
                    themeScores={data.themeScores}
                  />
                )
              })}
            </div>
          </div>

          {/* Resultats Management - 1/3 */}
          <div className="lg:col-span-1 print-avoid-break">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              Resultats Management
            </h2>
            {/* Card style unifié avec MetierSynthesisCard */}
            <Card className="overflow-hidden py-0">
              {/* Header style wizard comme card casques */}
              <div className="flex items-center justify-between px-5 py-4 bg-gray-50 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <span className="px-3 py-1.5 rounded-lg bg-purple-600 text-white text-sm font-semibold">
                    Management SST
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-3 py-1.5 rounded-lg text-sm font-bold text-white ${
                    (globalScores.maxQ2 > 0 ? Math.round((globalScores.scoreQ2 / globalScores.maxQ2) * 100) : 0) >= 70
                      ? 'bg-green-500'
                      : (globalScores.maxQ2 > 0 ? Math.round((globalScores.scoreQ2 / globalScores.maxQ2) * 100) : 0) >= 40
                      ? 'bg-orange-500'
                      : 'bg-red-500'
                  }`}>
                    {globalScores.maxQ2 > 0 ? Math.round((globalScores.scoreQ2 / globalScores.maxQ2) * 100) : 0}%
                  </span>
                  <span className="text-sm text-gray-500">
                    ({globalScores.scoreQ2}/{globalScores.maxQ2})
                  </span>
                </div>
              </div>

              <div className="p-5">
                {/* Zone principale : Radar + Top/Flop */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                  {/* Radar Management */}
                  <div className="flex justify-center">
                    <RadarChartManagement scores={scoresQ2Data.scores} maxScores={scoresQ2Data.maxScores} height={180} />
                  </div>

                  {/* Top 3 / Flop 3 Q2 par thèmes */}
                  <div className="space-y-3">
                    {/* Top 3 */}
                    <div>
                      <h4 className="text-xs font-semibold text-green-700 mb-1 flex items-center gap-1">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                        </svg>
                        Points forts
                      </h4>
                      <div className="space-y-1">
                        {q2Rankings.top3.length > 0 ? q2Rankings.top3.map((theme, i) => (
                          <div key={`top-${i}-${theme.themeId}`} className="flex items-center gap-2 text-xs">
                            <span className="w-4 h-4 bg-green-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                              {i + 1}
                            </span>
                            <span className="flex-1 truncate capitalize">{theme.themeName}</span>
                            <span className="font-medium text-green-700">{theme.percent}%</span>
                          </div>
                        )) : (
                          <p className="text-xs text-gray-400 italic">Aucune donnée</p>
                        )}
                      </div>
                    </div>

                    {/* Flop 3 */}
                    {q2Rankings.flop3.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-red-700 mb-1 flex items-center gap-1">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                          </svg>
                          A améliorer
                        </h4>
                        <div className="space-y-1">
                          {q2Rankings.flop3.map((theme, i) => (
                            <div key={`flop-${i}-${theme.themeId}`} className="flex items-center gap-2 text-xs">
                              <span className="w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                                {i + 1}
                              </span>
                              <span className="flex-1 truncate capitalize">{theme.themeName}</span>
                              <span className="font-medium text-red-700">{theme.percent}%</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Liste des 6 axes Management avec progression */}
                <div className="pt-3 border-t border-gray-100 space-y-2">
                  <h4 className="text-xs font-semibold text-gray-700 mb-2">
                    Progression par axe
                  </h4>
                  <ManagementProgressList
                    scores={scoresQ2Data.scores}
                    maxScores={scoresQ2Data.maxScores}
                    compact={true}
                  />
                </div>
              </div>
            </Card>
          </div>
        </div>

        {/* SECTION 5 & 6: HALTES consolide + Distribution (cote a cote) */}
        <div className="print-break-before grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Analyse HALTES consolidee */}
          <div className="print-avoid-break">
            <HaltesConsolidatedAnalysis metiersData={haltesConsolidatedData} />
          </div>

          {/* Distribution des reponses */}
          <Card className="print-avoid-break">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <span className="w-7 h-7 bg-gray-100 text-gray-700 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </span>
                Distribution des Réponses
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Distribution globale - Donut + Legende */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                {/* Donut chart */}
                <div className="flex flex-col items-center">
                  <div className="relative w-32 h-32">
                    <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                      <circle cx="50" cy="50" r="42" fill="none" stroke="#f3f4f6" strokeWidth="12" />
                      {(() => {
                        const total = Object.values(distribution).reduce((a, b) => a + b, 0)
                        if (total === 0) return null
                        const colors = ['#ef4444', '#f97316', '#eab308', '#22c55e']
                        let offset = 0
                        return [0, 1, 2, 3].map((level) => {
                          const count = distribution[level as 0 | 1 | 2 | 3]
                          const percent = (count / total) * 264
                          const segment = (
                            <circle
                              key={level}
                              cx="50"
                              cy="50"
                              r="42"
                              fill="none"
                              stroke={colors[level]}
                              strokeWidth="12"
                              strokeDasharray={`${percent} 264`}
                              strokeDashoffset={-offset}
                              className="transition-all duration-500"
                            />
                          )
                          offset += percent
                          return segment
                        })
                      })()}
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-xl font-bold text-gray-700">
                        {Object.values(distribution).reduce((a, b) => a + b, 0)}
                      </span>
                      <span className="text-xs text-gray-500">reponses</span>
                    </div>
                  </div>
                </div>

                {/* Legende avec valeurs */}
                <div className="space-y-2">
                  {[
                    { level: 3, label: 'En place', color: 'bg-green-500', textColor: 'text-green-700', bgColor: 'bg-green-50' },
                    { level: 2, label: 'En cours', color: 'bg-yellow-500', textColor: 'text-yellow-700', bgColor: 'bg-yellow-50' },
                    { level: 1, label: 'Planifié', color: 'bg-orange-500', textColor: 'text-orange-700', bgColor: 'bg-orange-50' },
                    { level: 0, label: 'Non prévu', color: 'bg-red-500', textColor: 'text-red-700', bgColor: 'bg-red-50' },
                  ].map(({ level, label, color, textColor, bgColor }) => {
                    const count = distribution[level as 0 | 1 | 2 | 3]
                    const total = Object.values(distribution).reduce((a, b) => a + b, 0)
                    const percent = total > 0 ? Math.round((count / total) * 100) : 0
                    return (
                      <div key={level} className={`flex items-center gap-3 p-2 rounded-lg ${bgColor}`}>
                        <div className={`w-3 h-3 rounded-full ${color}`} />
                        <span className={`flex-1 text-sm ${textColor}`}>{label}</span>
                        <span className={`text-sm font-bold ${textColor}`}>{count}</span>
                        <span className="text-xs text-gray-500 w-10 text-right">{percent}%</span>
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Distribution par metier - grille 1/3 2/3 3/3 */}
              {metierIds.length > 1 && (
                <div className="pt-4 border-t border-gray-100">
                  <h4 className="text-sm font-semibold text-gray-700 mb-4">Par metier</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {metiersSelectionnes.map((metier) => {
                      const dist = distributionByMetier.get(metier.code)
                      if (!dist) return null
                      const total = Object.values(dist).reduce((a, b) => a + b, 0)
                      return (
                        <div key={metier.code} className="bg-gray-50 rounded-lg p-3">
                          <p className="text-sm font-medium text-gray-900 mb-3 text-center">{metier.nom}</p>
                          {/* Donut centre */}
                          <div className="flex justify-center mb-3">
                            <div className="relative w-20 h-20">
                              <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                                <circle cx="50" cy="50" r="42" fill="none" stroke="#e5e7eb" strokeWidth="12" />
                                {(() => {
                                  if (total === 0) return null
                                  const colors = ['#ef4444', '#f97316', '#eab308', '#22c55e']
                                  let offset = 0
                                  return [0, 1, 2, 3].map((level) => {
                                    const count = dist[level as 0 | 1 | 2 | 3]
                                    const percent = (count / total) * 264
                                    const segment = (
                                      <circle
                                        key={level}
                                        cx="50"
                                        cy="50"
                                        r="42"
                                        fill="none"
                                        stroke={colors[level]}
                                        strokeWidth="12"
                                        strokeDasharray={`${percent} 264`}
                                        strokeDashoffset={-offset}
                                      />
                                    )
                                    offset += percent
                                    return segment
                                  })
                                })()}
                              </svg>
                              <div className="absolute inset-0 flex flex-col items-center justify-center">
                                <span className="text-sm font-bold text-gray-700">{total}</span>
                                <span className="text-xs text-gray-500">rep.</span>
                              </div>
                            </div>
                          </div>
                          {/* Legende compacte - meme style que global */}
                          <div className="space-y-1">
                            {[
                              { level: 3, label: 'En place', color: 'bg-green-500', textColor: 'text-green-700', bgColor: 'bg-green-50' },
                              { level: 2, label: 'En cours', color: 'bg-yellow-500', textColor: 'text-yellow-700', bgColor: 'bg-yellow-50' },
                              { level: 1, label: 'Planifié', color: 'bg-orange-500', textColor: 'text-orange-700', bgColor: 'bg-orange-50' },
                              { level: 0, label: 'N. prévu', color: 'bg-red-500', textColor: 'text-red-700', bgColor: 'bg-red-50' },
                            ].map(({ level, label, color, textColor, bgColor }) => {
                              const count = dist[level as 0 | 1 | 2 | 3]
                              const percent = total > 0 ? Math.round((count / total) * 100) : 0
                              return (
                                <div key={level} className={`flex items-center gap-2 p-1.5 rounded ${bgColor}`}>
                                  <div className={`w-2.5 h-2.5 rounded-full ${color}`} />
                                  <span className={`flex-1 text-xs ${textColor}`}>{label}</span>
                                  <span className={`text-xs font-bold ${textColor}`}>{count}</span>
                                  <span className="text-xs text-gray-500 w-8 text-right">{percent}%</span>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* SECTION 7: Plan d'Actions complet */}
        {actions.length > 0 && (
          <Card className="print-avoid-break">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Plan d'Actions
                <span className="ml-auto text-sm font-normal text-gray-500">
                  {actionsStats.completed}/{actionsStats.total} terminees
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Stats en ligne */}
              <div className="flex items-center gap-6 mb-6 pb-4 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                    <span className="text-lg font-bold text-gray-700">{actionsStats.total}</span>
                  </div>
                  <span className="text-sm text-gray-600">Total</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-lg font-bold text-green-600">{actionsStats.completed}</span>
                  </div>
                  <span className="text-sm text-gray-600">Terminees</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                    <span className="text-lg font-bold text-amber-600">{actionsStats.pending}</span>
                  </div>
                  <span className="text-sm text-gray-600">En cours</span>
                </div>
                {/* Barre de progression */}
                <div className="flex-1 ml-4">
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-green-500 transition-all"
                      style={{ width: `${actionsStats.total > 0 ? Math.round((actionsStats.completed / actionsStats.total) * 100) : 0}%` }}
                    />
                  </div>
                  <div className="text-xs text-gray-500 mt-1 text-right">
                    {actionsStats.total > 0 ? Math.round((actionsStats.completed / actionsStats.total) * 100) : 0}% complete
                  </div>
                </div>
              </div>

              {/* Liste complete des actions groupees par theme */}
              <div className="space-y-4">
                {/* Grouper les actions par theme */}
                {Array.from(new Set(actions.map(a => a.theme || 'Autre'))).map((theme) => {
                  const themeActions = actions.filter(a => (a.theme || 'Autre') === theme)
                  const themeCompleted = themeActions.filter(a => a.termineLe).length

                  return (
                    <div key={theme} className="print-avoid-break">
                      {/* Header du theme */}
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="text-sm font-semibold text-gray-900 capitalize">{theme}</h4>
                        <span className="text-xs text-gray-400">
                          ({themeCompleted}/{themeActions.length})
                        </span>
                        <div className="flex-1 h-px bg-gray-100" />
                      </div>

                      {/* Actions du theme */}
                      <div className="space-y-1.5 pl-2">
                        {themeActions.map((action) => (
                          <div
                            key={action.id}
                            className={`flex items-start gap-2 p-2 rounded-lg text-sm ${
                              action.termineLe ? 'bg-green-50' : 'bg-gray-50'
                            }`}
                          >
                            {/* Checkbox visuelle */}
                            <span
                              className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 ${
                                action.termineLe
                                  ? 'bg-green-500 border-green-500 text-white'
                                  : 'border-gray-300 bg-white'
                              }`}
                            >
                              {action.termineLe && (
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </span>

                            {/* Contenu */}
                            <div className="flex-1 min-w-0">
                              <p className={`${action.termineLe ? 'text-green-800 line-through' : 'text-gray-700'}`}>
                                {action.libelle}
                              </p>
                              {action.termineLe && (
                                <p className="text-xs text-green-600 mt-0.5">
                                  Termine le {new Date(action.termineLe).toLocaleDateString('fr-FR')}
                                </p>
                              )}
                            </div>

                          </div>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Avertissement DUERP */}
        {duerpNonFait && (
          <div className="no-print p-4 bg-destructive/10 border border-destructive/30 rounded-xl">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <div>
                <p className="text-sm font-semibold text-destructive">DUERP non renseigné</p>
                <p className="text-sm text-destructive/80 mt-0.5">
                  Le DUERP doit être au minimum planifié pour pouvoir générer l'attestation. Modifiez votre réponse dans le questionnaire Management.
                </p>
              </div>
            </div>
          </div>
        )}

      </div>
    </DiagnosticLayout>
  )
}
